/**
 * ErrorBoundary - catches uncaught React render errors.
 *
 * =====================================================================
 * WHY THIS EXISTS:
 * =====================================================================
 *
 * When a React component throws during render, React unmounts the
 * ENTIRE component tree - the user sees a blank white page with no
 * message and no way to recover. An ErrorBoundary catches these errors
 * and renders a friendly fallback UI instead.
 *
 * =====================================================================
 * HOW IT INTEGRATES WITH SENTINEL (v4.0):
 * =====================================================================
 *
 * When a crash is caught, this component POSTs the error details to
 * the backend endpoint ``POST /api/v1/ops/report-error/``. The backend's
 * ``FEErrorReportView`` feeds the error into the same Sentinel-compatible
 * alert dispatcher that handles backend 5xx errors. This means:
 *
 *   FE crash → ErrorBoundary → POST /report-error/ → AlertDispatcher
 *   → POST to HRM (LFB Scout) → task created for the team.
 *
 * The team sees frontend crashes in their project management system
 * alongside backend errors, with full context (component stack, URL,
 * user agent).
 *
 * =====================================================================
 * AUTH TOKEN LOOKUP (v4.0.1 fix):
 * =====================================================================
 *
 * TNN stores auth state in localStorage under the key ``"tnn.auth"``
 * as a JSON object: ``{ accessToken, user, tenantSlug }``. The error
 * report includes the Bearer token so the backend knows which user
 * experienced the crash. If the token is missing or expired, the
 * request is still sent - the backend logs it even without auth.
 *
 * IMPORTANT: the localStorage key is ``"tnn.auth"``, NOT ``"access_token"``
 * or ``"auth"``. A v4.0 bug had the wrong key, causing every error
 * report to fail with 401.
 *
 * =====================================================================
 * REACT CONSTRAINT:
 * =====================================================================
 *
 * Error boundaries MUST be class components. React does not provide a
 * Hook equivalent for ``getDerivedStateFromError`` / ``componentDidCatch``.
 * This is the only class component in the TNN frontend.
 */
import { Component, type ErrorInfo, type ReactNode } from "react";

import { Button, Result } from "antd";

import { v1Url } from "@/shared/lib/authorizedFetch";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/** Catches uncaught render errors and shows a friendly fallback instead of a blank page. */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("Uncaught render error:", error, info.componentStack);
    // v3.9: report the crash to the backend alert pipeline so it
    // creates a task in the team's project management system (Asana,
    // Slack, etc.) automatically. Best-effort - if the POST fails
    // (e.g. auth expired), the console.error above still fires.
    try {
      // TNN stores auth state as JSON under "tnn.auth" in localStorage.
      // Extract the accessToken for the error report. Best-effort - // if auth is expired or missing, the report still fires (the
      // backend can accept unauthenticated error reports from the
      // error boundary if we loosen the permission later).
      let token = "";
      try {
        const raw = localStorage.getItem("tnn.auth");
        if (raw) {
          const parsed = JSON.parse(raw);
          token = parsed?.accessToken || "";
        }
      } catch {
        // JSON parse failed - no token available
      }
      fetch(v1Url("/ops/report-error/"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          error: `${error.name}: ${error.message}`,
          component_stack: info.componentStack || "",
          url: window.location.href,
          user_agent: navigator.userAgent,
        }),
      }).catch(() => {/* best-effort - don't recurse on failure */});
    } catch {
      // Silently ignore - the console.error above is the fallback.
    }
  }

  private handleReload = (): void => {
    window.location.assign("/");
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <Result
          status="error"
          title="Something went wrong"
          subTitle="This page hit an unexpected error. Reloading usually fixes it. If it keeps happening, let your administrator know."
          extra={
            <Button type="primary" onClick={this.handleReload}>
              Reload the app
            </Button>
          }
        />
      );
    }
    return this.props.children;
  }
}
